/**
 * Générateur — Résoudre des problèmes impliquant des horaires, des durées
 * Attendu ID : 651 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, texte, texte_trou_saisie
 * Labels de référence :
 *   - Résoudre un problème multi-étapes d'horaires (séquence d'activités)
 *   - Exploiter un tableau d'horaires (bus, course, relais)
 * Variantes estimées : 1000+
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 651;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PROB = 'Résoudre un problème d\'horaires multi-étapes ou avec tableau';

const PRENOMS = [
    { nom: 'Adam', fem: false }, { nom: 'Amélie', fem: true }, { nom: 'Chloé', fem: true },
    { nom: 'Hugo', fem: false }, { nom: 'Inès', fem: true }, { nom: 'Jade', fem: true },
    { nom: 'Karim', fem: false }, { nom: 'Léo', fem: false }, { nom: 'Lucas', fem: false },
    { nom: 'Manon', fem: true }, { nom: 'Sofia', fem: true }, { nom: 'Thomas', fem: false },
    { nom: 'Yanis', fem: false },
];

// ─── Helpers horaires ───
const pad = (n) => String(n).padStart(2, '0');
const fmtH = (h, m) => `${h} h ${pad(m)}`;
const fmtD = (h, m) => {
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${pad(m)} min`;
};
const normalize = (h, m) => {
    while (m >= 60) { m -= 60; h += 1; }
    while (m < 0) { m += 60; h -= 1; }
    return { h, m };
};

// ─── Pool d'activités pour multi-étapes ───

const ACTIVITES = [
    { label: 'marche jusqu\'à la maison', minMin: 10, maxMin: 30 },
    { label: 'fait ses devoirs', minMin: 30, maxMin: 90 },
    { label: 'goûte', minMin: 10, maxMin: 20 },
    { label: 'va au sport', minMin: 45, maxMin: 105 },
    { label: 'regarde un épisode de série', minMin: 20, maxMin: 50 },
    { label: 'prend le bus', minMin: 15, maxMin: 40 },
    { label: 'joue dehors', minMin: 30, maxMin: 60 },
    { label: 'prend sa douche', minMin: 10, maxMin: 20 },
    { label: 'lit un livre', minMin: 20, maxMin: 45 },
    { label: 'aide à préparer le dîner', minMin: 20, maxMin: 45 },
    { label: 'fait du vélo', minMin: 20, maxMin: 60 },
    { label: 'joue aux jeux vidéo', minMin: 20, maxMin: 45 },
];

/** Génère un problème multi-étapes */
const genMultiEtapes = () => {
    const p = randomChoice(PRENOMS);
    const nbEtapes = randInt(2, 3);
    const activites = shuffle([...ACTIVITES]).slice(0, nbEtapes);

    // Sortie de cours réaliste (revue 2026-08-01) : « quitte le collège à 8 h »
    // pour aller goûter n'avait pas de sens. Borne haute inchangée : 17 h 55
    // + 4 h 15 d'activités max = 22 h 10, le distracteur « +1 h » reste < 24 h.
    const startH = randInt(13, 17);
    const startM = randInt(0, 11) * 5; // multiples de 5 pour lisibilité

    const etapes = [];
    let curH = startH, curM = startM;

    for (const act of activites) {
        const totalMin = randInt(Math.ceil(act.minMin / 5), Math.floor(act.maxMin / 5)) * 5;
        const durH = Math.floor(totalMin / 60);
        const durM = totalMin % 60;
        const next = normalize(curH + durH, curM + durM);
        etapes.push({
            label: act.label,
            durH, durM,
            startH: curH, startM: curM,
            endH: next.h, endM: next.m,
        });
        curH = next.h;
        curM = next.m;
    }

    if (curH > 23) return genMultiEtapes();

    // Construire l'énoncé : « Jade quitte le collège à 16 h 05. Elle marche…
    // Puis elle goûte… Ensuite elle lit… » (pronom dès la 2e phrase, revue 2026-08-01)
    const pronom = p.fem ? 'elle' : 'il';
    const pronomMaj = p.fem ? 'Elle' : 'Il';
    let enonce = `${p.nom} quitte le collège à ${fmtH(startH, startM)}. `;
    for (let i = 0; i < etapes.length; i++) {
        const e = etapes[i];
        const debut = i === 0 ? pronomMaj : (i === 1 ? `Puis ${pronom}` : `Ensuite ${pronom}`);
        enonce += `${debut} ${e.label} pendant ${fmtD(e.durH, e.durM)}. `;
    }
    enonce += `À quelle heure ${p.fem ? 'a-t-elle' : 'a-t-il'} fini ?`;

    return { p, etapes, startH, startM, finalH: curH, finalM: curM, enonce };
};

/** Génère un tableau d'horaires de bus */
const genTableauBus = () => {
    const p = randomChoice(PRENOMS);
    const heureActuelleH = randInt(7, 19);
    const heureActuelleM = randInt(0, 59);

    // Générer 5 bus avec des horaires proches
    const busNums = shuffle([70, 112, 179, 185, 210, 303, 321, 325, 44, 88]).slice(0, 5);
    const bus = busNums.map(num => {
        const offsetMin = randInt(-15, 35);
        const { h, m } = normalize(heureActuelleH, heureActuelleM + offsetMin);
        return { num, h, m };
    });

    // Horaires tous distincts et ≠ heure actuelle (revue 2026-08-01) : sinon deux
    // bus ex aequo donnent deux bonnes réponses, et un bus « pile maintenant »
    // rend « le prochain bus » ambigu pour l'élève.
    const nowMin = heureActuelleH * 60 + heureActuelleM;
    const timesMin = bus.map(b => b.h * 60 + b.m);
    if (new Set(timesMin).size !== bus.length || timesMin.includes(nowMin)) return genTableauBus();

    // Trouver le prochain bus (après heure actuelle)
    const futurs = bus.filter(b => b.h * 60 + b.m > nowMin);
    if (futurs.length === 0) return genTableauBus();

    futurs.sort((a, b) => (a.h * 60 + a.m) - (b.h * 60 + b.m));
    const prochainBus = futurs[0];
    const attenteMin = (prochainBus.h * 60 + prochainBus.m) - nowMin;

    // Construire le tableau HTML
    let table = '<table style="border-collapse:collapse;margin:8px auto">';
    table += '<tr style="background:#e8f4fd"><th style="border:1px solid #1e293b;padding:4px 10px">Bus</th><th style="border:1px solid #1e293b;padding:4px 10px">Départ</th></tr>';
    for (const b of bus) {
        table += `<tr><td style="border:1px solid #1e293b;padding:4px 10px">${b.num}</td><td style="border:1px solid #1e293b;padding:4px 10px">${fmtH(b.h, b.m)}</td></tr>`;
    }
    table += '</table>';

    return { p, bus, heureActuelleH, heureActuelleM, prochainBus, attenteMin, table };
};

// ═══════════════════════════════════════════
// EXERCICE — probleme_horaire
// ═══════════════════════════════════════════

const genProbQcm = () => {
    const isMulti = Math.random() < 0.6;

    if (isMulti) {
        const data = genMultiEtapes();
        const correct = fmtH(data.finalH, data.finalM);

        const dSet = new Set();
        // Oubli dernière étape
        if (data.etapes.length >= 2) {
            const avantDernier = data.etapes[data.etapes.length - 2];
            dSet.add(fmtH(avantDernier.endH, avantDernier.endM));
        }
        // ±1h
        dSet.add(fmtH(data.finalH + 1, data.finalM));
        dSet.add(fmtH(data.finalH - 1, data.finalM));
        // Retenue mal gérée (revue 2026-08-01) : si la dernière étape déborde
        // (minutes ≥ 60, y compris 60 pile), la vraie erreur élève est le
        // résultat NON CONVERTI (« 15 h 60 ») — même geste que la fiche 650.
        // Ses minutes ≥ 60 le rendent incollisionnable avec les autres options.
        const last = data.etapes[data.etapes.length - 1];
        const brutM = last.startM + last.durM;
        if (brutM >= 60) {
            dSet.add(fmtH(last.startH + last.durH, brutM));
        } else {
            // Filet (revue 2026-08-01) : l'élève additionne TOUTES les minutes
            // sans jamais convertir → « 17 h 85 ». N'existe que si le total brut
            // ≥ 60 (sinon il coïncide avec la bonne réponse) ; à défaut, +10 min.
            const brutTotalH = data.startH + data.etapes.reduce((s, e) => s + e.durH, 0);
            const brutTotalM = data.startM + data.etapes.reduce((s, e) => s + e.durM, 0);
            if (brutTotalM >= 60) {
                dSet.add(fmtH(brutTotalH, brutTotalM));
            } else {
                const n = normalize(data.finalH, data.finalM + 10);
                dSet.add(fmtH(n.h, n.m));
            }
        }
        dSet.delete(correct);

        const distracteurs = shuffle([...dSet].filter(d => !d.includes('-'))).slice(0, 3);
        while (distracteurs.length < 3) distracteurs.push(fmtH(data.finalH + randInt(-2, 2), randInt(0, 55)));

        const texte = `<p>${data.enonce}</p>`;
        const choices = [correct, ...distracteurs.slice(0, 3)];
        const payload = makeQcm(choices, correct, texte, opts(L_PROB));
        // sousType structurel → coup de pouce ciblé ; calcul en base 60, la
        // calculatrice (base 10) est piégeuse (revue 2026-08-01)
        payload.contenu = { ...payload.contenu, sousType: 'etapes', calculatrice: false };
        payload._meta.generatorKey = 'probleme_horaire';
        return payload;
    }

    // Tableau de bus
    const data = genTableauBus();
    const correct = `Bus ${data.prochainBus.num}`;

    // Distracteurs : autres bus (passés ou plus loin)
    const autres = data.bus.filter(b => b.num !== data.prochainBus.num);
    const distracteurs = shuffle(autres).slice(0, 3).map(b => `Bus ${b.num}`);
    while (distracteurs.length < 3) distracteurs.push(`Bus ${randInt(100, 400)}`);

    const texte = `<p>Il est ${fmtH(data.heureActuelleH, data.heureActuelleM)}. Voici les horaires des bus :</p>${data.table}<p>Quel est le prochain bus ?</p>`;
    const choices = [correct, ...distracteurs.slice(0, 3)];
    const payload = makeQcm(choices, correct, texte, opts(L_PROB));
    payload.contenu = { ...payload.contenu, sousType: 'bus', calculatrice: false };
    payload._meta.generatorKey = 'probleme_horaire';
    return payload;
};

const genProbTexte = () => {
    const isMulti = Math.random() < 0.5;

    if (isMulti) {
        // Saisie horaire libre → deux champs numériques tolérants (revue 2026-08-01,
        // même remède que 650 : « 18h05 » tapé tel quel était rejeté par le strict).
        let data = genMultiEtapes();
        // Garde anti-échange : si heures == minutes, l'inversion des deux champs
        // serait indétectable par la tolérance numérique.
        while (data.finalH === data.finalM) data = genMultiEtapes();
        const texte = `<p>${data.enonce}</p>`;
        // Espaces insécables autour des unités : sur mobile, la ligne ne doit
        // jamais casser entre un champ et son « h » (revue 2026-08-01)
        const phrase = `Réponse : {{${data.finalH}}} h {{${pad(data.finalM)}}}.`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_PROB), mode: 'saisie', strict: false });
        payload.contenu = { ...payload.contenu, sousType: 'etapes', calculatrice: false };
        payload._meta.generatorKey = 'probleme_horaire';
        return payload;
    }

    // Tableau de bus — demander le temps d'attente
    const data = genTableauBus();
    const texte = `<p>Il est ${fmtH(data.heureActuelleH, data.heureActuelleM)}. Voici les horaires des bus :</p>${data.table}<p>Combien de minutes dois-tu attendre le prochain bus ?</p>`;
    const payload = makeSaisie(String(data.attenteMin), texte, { ...opts(L_PROB), strict: true, suffixe: 'min' });
    payload.contenu = { ...payload.contenu, sousType: 'bus', calculatrice: false };
    payload._meta.generatorKey = 'probleme_horaire';
    return payload;
};

const genProbTrou = () => {
    const data = genMultiEtapes();

    // Espaces insécables dans les tokens horaires et autour des trous : sur
    // mobile, pas de casse entre un champ et son « h » (revue 2026-08-01)
    const nb = (s) => s.replace(/ /g, ' ');
    let phrase = '';
    for (let i = 0; i < data.etapes.length; i++) {
        const e = data.etapes[i];
        const dureeStr = fmtD(e.durH, e.durM);
        phrase += `Étape ${i + 1} : ${nb(fmtH(e.startH, e.startM))} + ${nb(dureeStr)} = {{${e.endH}}} h {{${pad(e.endM)}}}.\n`;
    }
    phrase = phrase.trim();

    const texte = `<p>${data.enonce} Complète le calcul étape par étape.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PROB), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, sousType: 'etapes', calculatrice: false };
    payload._meta.generatorKey = 'probleme_horaire';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    probleme_horaire_qcm_unique:       genProbQcm,
    probleme_horaire_texte:            genProbTexte,
    probleme_horaire_texte_trou_saisie: genProbTrou,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'probleme_horaire', label: L_PROB, formatsList: buildFormats('probleme_horaire') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
